// Thin fetch client. The access token lives only in memory; the refresh token is an
// httpOnly cookie the browser sends to /api/v1/auth/refresh with a CSRF header.
import type { ApiErrorBody, ErrorCode, SessionResponse } from '@zenarty/shared';

const BASE = '/api/v1';

let accessToken: string | null = null;
let refreshing: Promise<boolean> | null = null;
const listeners = new Set<() => void>();

export function setAccessToken(token: string | null) {
  accessToken = token;
}

/** Called when the session ends (refresh failed) so the UI can return to login. */
export function onSessionLost(fn: () => void) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: ErrorCode | 'NETWORK',
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
  }
}

function csrfToken(): string | null {
  const match = /(?:^|;\s*)(?:__Host-)?wms_csrf=([^;]+)/.exec(document.cookie);
  return match ? decodeURIComponent(match[1]!) : null;
}

async function toError(res: Response): Promise<ApiRequestError> {
  try {
    const body = (await res.json()) as ApiErrorBody;
    return new ApiRequestError(res.status, body.error.code, body.error.message, body.error.details);
  } catch {
    return new ApiRequestError(res.status, 'INTERNAL', `Request failed (${res.status})`);
  }
}

/** Single-flight refresh: concurrent 401s share one rotation (rotation is one-shot). */
export function refreshSession(): Promise<boolean> {
  refreshing ??= (async () => {
    try {
      const csrf = csrfToken();
      if (!csrf) return false;
      const res = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
        body: '{}',
      });
      if (!res.ok) return false;
      const session = (await res.json()) as SessionResponse;
      accessToken = session.accessToken;
      return true;
    } catch {
      return false;
    } finally {
      setTimeout(() => (refreshing = null), 0);
    }
  })();
  return refreshing;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Bearer token to use instead of the session (MFA challenge steps). */
  token?: string;
}

export async function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const method = opts.method ?? 'GET';
  // One key per logical write, reused on the retry after a token refresh.
  const idempotencyKey = method === 'GET' ? undefined : crypto.randomUUID();
  const send = () => {
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
    const token = opts.token ?? accessToken;
    if (token) headers.Authorization = `Bearer ${token}`;
    if (idempotencyKey && !path.startsWith('/auth/')) headers['Idempotency-Key'] = idempotencyKey;
    return fetch(`${BASE}${path}`, {
      method,
      headers,
      credentials: 'same-origin',
      body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
    });
  };

  let res: Response;
  try {
    res = await send();
    if (res.status === 401 && !opts.token && accessToken) {
      if (await refreshSession()) res = await send();
      else listeners.forEach((fn) => fn());
    }
  } catch {
    throw new ApiRequestError(0, 'NETWORK', 'Cannot reach the server. Check your connection.');
  }
  if (!res.ok) throw await toError(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** Downloads a CSV (or other text) endpoint as a file, with the session token. */
export async function downloadFile(path: string, filename: string): Promise<void> {
  const send = () =>
    fetch(`${BASE}${path}`, {
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
      credentials: 'same-origin',
    });
  let res = await send();
  if (res.status === 401 && (await refreshSession())) res = await send();
  if (!res.ok) throw await toError(res);
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Fetches a binary endpoint (photos) with the session token, as an object URL. */
export async function fetchObjectUrl(path: string): Promise<string> {
  const send = () =>
    fetch(`${BASE}${path}`, {
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
      credentials: 'same-origin',
    });
  let res = await send();
  if (res.status === 401 && (await refreshSession())) res = await send();
  if (!res.ok) throw await toError(res);
  return URL.createObjectURL(await res.blob());
}

/** Reads a File as base64 (no data: prefix) for JSON uploads. */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () =>
      resolve(typeof r.result === 'string' ? r.result.replace(/^data:[^,]*,/, '') : '');
    r.onerror = () => reject(new Error('Could not read the file'));
    r.readAsDataURL(file);
  });
}
